import {CSSProperties, ForwardedRef, MutableRefObject, ReactNode, RefCallback, useState} from 'react'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {Label} from '@/components/ui/label/Label'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectItem,
    SelectTrigger
} from '@/components/ui/select-atoms/SelectAtoms'
import {useTranslation} from '@/translations/i18n'
import {genericForwardRef} from '@/utilities/helpers'
import {ComboboxItem, ComboboxProvider, SelectProvider} from '@ariakit/react'

interface InputSelectProps<T extends string = string> {
    onChange: (value: T) => void
    options: Array<{
        label: string
        value: T
    }>
    label?: string
    placeholder?: string
    value?: T
    errorMessage?: string
    displayValue?: (label: string) => ReactNode
    onClear?: () => void
    isOptional?: boolean
    sameWidth?: boolean
    disabled?: boolean
    disabledOptions?: T[]
    isPortal?: boolean
    portalRef?: RefCallback<HTMLElement> | MutableRefObject<HTMLElement | null>
    triggerClassName?: string
    hasItemCheckbox?: boolean
    style?: CSSProperties
}

export const InputSelectCombobox = genericForwardRef(
    <T extends string = string>(
        {
            label,
            value,
            placeholder,
            isOptional,
            onClear,
            displayValue,
            errorMessage,
            onChange,
            options,
            sameWidth,
            isPortal,
            portalRef,
            disabled,
            disabledOptions,
            triggerClassName,
            hasItemCheckbox = true,
            style
        }: InputSelectProps<T>,
        ref: ForwardedRef<HTMLSelectElement>
    ) => {
        const {t} = useTranslation()
        const [search, setSearch] = useState('')

        return (
            <ComboboxProvider value={search ?? ''} setValue={setSearch}>
                <SelectProvider value={value ?? ''} setValue={onChange}>
                    <Flexbox direction="column" gap={1.5} fullWidth style={style}>
                        {label && (
                            <Label>
                                {label} {isOptional && <span className="description">({t('commons:optional')})</span>}
                            </Label>
                        )}
                        <SelectTrigger
                            ref={ref}
                            className={triggerClassName}
                            placeholder={placeholder ?? t('commons:select')}
                            value={value}
                            fullWidth
                            disabled={disabled}
                            hasError={!!errorMessage}
                            displayValue={value =>
                                typeof value == 'string' && (
                                    <Flexbox justify="space-between" align="center" fullWidth gap={2}>
                                        {displayValue
                                            ? displayValue(options.find(option => option.value === value)?.label ?? '')
                                            : options.find(option => option.value === value)?.label ?? value}
                                        {onClear && <XCloseIcon role="button" onClick={onClear} />}
                                    </Flexbox>
                                )
                            }
                        />
                        {errorMessage && (
                            <Flexbox align="center" gap={errorMessage ? 1.5 : 0}>
                                <InputStatusIcon hasError={!!errorMessage} />
                                <InputHelpText error={errorMessage} />
                            </Flexbox>
                        )}
                        <SelectComboboxPopover portal={!!isPortal} portalRef={portalRef} sameWidth={sameWidth}>
                            <SelectComboboxInput />
                            <SelectComboboxList>
                                {options
                                    .filter(option => option.label.toLowerCase().includes(search.toLowerCase()))
                                    .map((option, index) => (
                                        <SelectItem
                                            disabled={disabledOptions?.includes(option.value)}
                                            hasItemCheckbox={hasItemCheckbox}
                                            key={index}
                                            value={option.value}
                                            render={<ComboboxItem />}
                                        >
                                            {option.label}
                                        </SelectItem>
                                    ))}
                            </SelectComboboxList>
                        </SelectComboboxPopover>
                    </Flexbox>
                </SelectProvider>
            </ComboboxProvider>
        )
    }
)
